import { Clickable } from '@carvana/merch-components';
import cx from 'classnames';
import { useContext, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { type Tag as TagType } from '@/srp/MainLayout/constants';
import FilterEvents from '~/constants/srp/events';
import SearchSource from '~/constants/srp/SearchSource';
import { useIsMobile } from '~/context/common/DeviceContext';
import AppliedFiltersContext from '~/context/srp/AppliedFiltersContext';
import FacetDataContext from '~/context/srp/FacetDataContext';
import { useTrackEvent } from '~/context/srp/SearchAnalyticsContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import Tag from './Tag';
import useGetFilterTags from '../useGetFilterTags';

const MAX_TAGS = 5;

function shouldBeExpanded(tags: TagType[]): boolean {
  return tags.length <= MAX_TAGS;
}

export default function AppliedFilters() {
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const { baseline } = useContext(FacetDataContext);
  const { appliedFilters, clearAllFilters } = useContext(AppliedFiltersContext);
  const filterTags = useGetFilterTags(baseline, appliedFilters);
  const isMobile = useIsMobile();
  const [clearAll, setClearAll] = useState(false);
  const [isExpanded, setExpanded] = useState(shouldBeExpanded(filterTags));
  const canExpand = !isMobile && filterTags.length > MAX_TAGS;
  const trackEvent = useTrackEvent();

  const handleClearAllTags = () => {
    setSearchSource(SearchSource.TagRemoved);
    setClearAll(true);
    clearAllFilters();
    trackEvent(FilterEvents.removedFilter, {
      item: 'clear all button',
      label: 'Clear All'
    });
  };

  const showAppliedTags = filterTags.length > 0 && !clearAll;

  useEffect(() => {
    setClearAll(false);
  }, [filterTags, appliedFilters]);

  useEffect(() => {
    setExpanded(isMobile || shouldBeExpanded(filterTags));
  }, [filterTags, isMobile]);

  return (
    <AnimateHeight
      className={cx({ 'border-b border-grey-2': showAppliedTags })}
      duration={500}
      height={showAppliedTags ? 'auto' : 0}
      data-qa="applied-filters"
    >
      <div className="flex flex-col bg-grey-1 px-0 pt-24 l:rounded-t-md l:px-32">
        <div className="mb-16 flex w-full flex-row justify-between px-32 leading-5 l:px-0">
          <span className="uppercase text-blue-6 tl-header-s">
            <span className="m:hidden">Applied</span> Filters (
            {filterTags.length})
          </span>
          <Clickable
            className="text-grey-5 tl-link-s"
            data-analytics-id={null}
            data-qa="clear-all-applied-filters"
            element="span"
            onClick={handleClearAllTags}
          >
            Clear All
          </Clickable>
        </div>

        <div className="flex w-full gap-8 overflow-x-auto px-32 pb-24 l:flex-wrap l:px-0">
          {filterTags.slice(0, isExpanded ? undefined : MAX_TAGS).map(tag => (
            <Tag key={`${tag.facetKey}-${tag.tagName}`} filterTag={tag} />
          ))}

          {canExpand && (
            <Clickable
              className="flex items-center py-[9px] text-blue-2 tl-label-m"
              onClick={() => {
                setExpanded(v => !v);
              }}
              data-analytics-id="view-all-applied-filters"
            >
              Show {isExpanded ? 'Fewer' : `${filterTags.length - 5} More`}
            </Clickable>
          )}
        </div>
      </div>
    </AnimateHeight>
  );
}
